import { render } from "preact";
import { type Signal, signal } from "@preact/signals";
import { type Credit, FETCHED, LIBRARIES } from "./licenses.ts";
import styles from "./credits.module.css";

/** The credits, opened by the game. */
export interface Credits {
  /** Shows the credits; `onClose` runs when the player closes them. */
  open(onClose: () => void): void;
  /** The way back, as 閉じる goes (null while they are not shown). */
  readonly wayBack: (() => void) | null;
}

/**
 * Who made the game, the libraries it is built on, and what it fetches to look at a
 * request's air, with their licenses.
 */
export function mountCredits(root: HTMLElement): Credits {
  const open = signal(false);
  let onClose = () => {};
  const close = () => {
    open.value = false;
    onClose();
  };
  render(<CreditsScreen open={open} close={close} />, root);
  return {
    open(back) {
      onClose = back;
      open.value = true;
    },
    get wayBack() {
      return open.value ? close : null;
    },
  };
}

function CreditsScreen({ open, close }: { open: Signal<boolean>; close: () => void }) {
  if (!open.value) return null;
  return (
    <div class={styles.credits}>
      <div class={styles.card}>
        <h2 class={styles.heading}>クレジット</h2>
        <img class={styles.logo} src="logo.svg" alt="Kneading Needed -こねこのこねこね工房-" />
        <dl class={styles.staff}>
          <dt>制作</dt>
          <dd>mfakane</dd>
        </dl>
        <p class={styles.groupTitle}>使わせてもらっているもの</p>
        <Libraries items={LIBRARIES} />
        <p class={styles.groupTitle}>雰囲気を見るときに読み込むもの</p>
        <Libraries items={FETCHED} />
        <div class={`row ${styles.actions}`}>
          <button type="button" class="reset" onClick={close}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

/** Each library's name, version and license, with the license's text to open. */
function Libraries({ items }: { items: Credit[] }) {
  return (
    <ul class={styles.libraries}>
      {items.map((lib) => (
        <li key={lib.name}>
          <div class={styles.libraryHead}>
            <a href={lib.url} target="_blank" rel="noopener noreferrer">{lib.name}</a>
            <span>{lib.version}</span>
            <span class={styles.license}>{lib.licenseName}</span>
          </div>
          <details class={styles.text}>
            <summary>ライセンス全文</summary>
            {
              // The text as shipped, its lines joined back into paragraphs.
              lib.license.trim().split(/\n\s*\n/).map((paragraph, k) => (
                <p key={k}>{paragraph.replace(/\s*\n\s*/g, " ")}</p>
              ))
            }
          </details>
        </li>
      ))}
    </ul>
  );
}
